import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import styled from 'styled-components'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { shallowEqual, useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { urlTransform } from 'lib/string/removeSpaceUtils'
import HeadData from 'components/Common/HeadData'
import type { SearchFormRef } from 'components/Search'
import {
  getRelatedTravelItems,
} from 'lib/offer/destinationProductPageUtils'
import LayoutContainer from 'components/Common/LayoutContainer'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import HttpStatus from 'components/Common/HttpStatus'
import { fetchPlaceByName } from 'actions/PlaceActions'
import Faq from 'components/Common/Faq'
import Breadcrumbs from 'components/Pages/DestinationProductPage/Breadcrumbs'
import SearchForm from 'components/Search/SearchForm'
import { isSearchEnabled, decodeSearchParams, getSearchKey } from 'lib/search/searchUtils'
import type { DestinationNavItemType } from 'components/Common/TopDestinationsList/TopDestinationList'
import TopDestinationList from 'components/Common/TopDestinationsList/TopDestinationList'
import { useGlobalHotelsSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalHotelsSearch'
import { GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import type { OfferListAndAction, OfferListV2Item } from 'components/OfferList/OfferList'
import OfferList, { OfferListAction } from 'components/OfferList/OfferList'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import useOfferList from 'hooks/Offers/useOfferList'
import useOffer from 'hooks/Offers/useOffer'
import { ANYWHERE_PLACE_ID, HOTEL_SEARCH_TYPEAHEAD_TYPES } from 'constants/search'
import { getListName } from 'analytics/enhanced-ecommerce'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'
import {
  OfferListEventsProvider,
  OfferListEvents,
} from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useOfferListSortOptions from 'hooks/useOfferListSortOptions'
import { mediaQueryUp } from '../../utils/breakpoint'
import type { FilterControlChangeHandler } from '../HotelSearchPage/HotelSearchPageFilterPane'
import HotelSearchPageFilterPane from '../HotelSearchPage/HotelSearchPageFilterPane'
import type { EnabledFilters } from '../../Search/type'
import { defaultEnabledFilters } from '../../Search/type'
import { pushWithRegion, replaceSearchString } from 'actions/NavigationActions'
import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import { deleteSearchParams, parseSearchString } from 'lib/url/searchUrlUtils'
import SearchField from '../HotelSearchPage/SearchField'
import useGlobalSearchURLHashVertical from '../../../hooks/GlobalSearch/useGlobalSearchURLHashVertical'
import useQueryParams from '../../../hooks/useQueryParams'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import getOfferListKey from 'lib/offer/offerListKey'
import switchFunc from 'lib/function/switchFunc'
import { searchEvent } from 'analytics/snowplow/events'
import { trackEvent } from 'analytics/analytics'
import DestinationPageRelatedTravelItems from '../DestinationProductPage/Support/DestinationPageRelatedTravelItems'
import qs from 'qs'
import OfferCountAndSort from '../HotelSearchPage/OfferCountAndSort'
import FaqWrapper from '../SearchPage/FaqWrapper'
import { useMultipleOfferListFilters } from 'hooks/Offers/useMultipleOfferListFilters'
import type { SortControlChangeHandler } from 'components/Common/OfferSortControl/OfferSortControl'
import { fetchOfferList } from 'actions/OfferActions'
import { nonNullable, unique } from 'lib/array/arrayUtils'
import TextButton from 'LuxKit/Button/TextButton'
import Typography from 'LuxKit/Typography/Typography'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useSearchAnalytics from 'hooks/Analytics/useSearchAnalytics'
import { mapGlobalSearchContextToSnowplowSearchEvent } from 'analytics/mapSnowplowSearchTracking'
import useSearchEvent from 'hooks/Search/useSearchEvent'
import Sheet from 'components/utils/Sheet'
import { setSearchEvent } from 'actions/SearchActions'

const productTypeToSearchVertical = switchFunc<App.SearchVertical>({
  Hotels: 'hotels',
  Tours: 'tours',
}, 'hotels')

const StyledLayoutContainer = styled(LayoutContainer)`
  &.filterPane {
    background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
    display: grid;
    padding: ${rem(24)} 0;
    grid-template-columns: minmax(0, 1fr);
    grid-gap: ${rem(32)};

    ${mediaQueryUp.desktop} {
      grid-template: "offer-filters offer-list" auto / ${rem(278)} minmax(0, 2fr);
      padding-top: ${rem(12)};
    }
  }

  &.fullWidth {
    padding: ${rem(16)} 0;
  }

  &.largePadding{
    padding-top: ${rem(46)};

    ${mediaQueryUp.tablet} {
      padding-top: ${rem(20)};
    }

  }
`

const StyledHotelSearchPageFilterPane = styled(HotelSearchPageFilterPane)`
  grid-area: offer-filters;
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  grid-area: offer-list;
`

const DesktopContainer = styled.div`
  ${mediaQueryUp.tablet} {
    padding: 0 ${rem(10)} ${rem(16)} ${rem(10)};
    border-bottom: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    margin-top: ${rem(16)};
  }
`

const MobileStyledSearchField = styled(SearchField)`
  max-width: 100%;
  padding-top: ${rem(12)};
  padding-bottom: 0;
  display: block;
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

interface Props {
  regionCode: string
  isSearchEnabled: boolean
  locationToPlace: { [location: string]: App.Place }
  placesLoading: { [location: string]: boolean }
  customHeadings?: App.ContentHeadings
  filtersFromUrl: App.OfferListFilters
  windowSearch: string
  offerListsGroup: Array<App.ContentfulOfferList>
}

function AdsLandingPage({
  regionCode,
  isSearchEnabled,
  locationToPlace,
  placesLoading,
  windowSearch,
  offerListsGroup,
  filtersFromUrl,
}: Props) {
  const intl = useIntl()
  const { searchSortByTypes } = useOfferListSortOptions()
  const location = offerListsGroup[0].location
  const productType = 'Hotels'
  const formRef = useRef<SearchFormRef>(null)

  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  const onBedbankSelectDatesPrompt = useCallback(() => {
    formRef.current?.form.current.scrollIntoView()
    formRef.current?.openDatePickerMenu()
  }, [])

  const handleSearchFormSubmit = useCallback(() => {
    formRef.current?.form.current?.dispatchEvent(
      new Event('submit', { cancelable: true, bubbles: true }),
    )
  }, [])

  const place = locationToPlace[offerListsGroup[0].location]
  const placeLoading = location ? placesLoading[location] : undefined
  const placeId = place?.id

  const searchUrl = useMemo(() => {
    const params = qs.parse(windowSearch, { ignoreQueryPrefix: true })
    params.destinationName = offerListsGroup[0].location
    params.destinationId = placeId
    return `?${qs.stringify(params)}`
  }, [placeId, offerListsGroup, windowSearch])

  const decodedSearchParams = useMemo(() => decodeSearchParams(searchUrl), [searchUrl])

  const canonicalName = place?.canonicalName
  const searchFormEnabled = isSearchEnabled
  const searchVertical = productTypeToSearchVertical(productType)
  const searchItemPlaceId = decodedSearchParams?.searchItem?.value || placeId || ''

  const searchItem: App.SearchItem = useMemo(() => (
    {
      searchType: 'destination',
      value: searchItemPlaceId,
      placeId: searchItemPlaceId,
      format: ({
        mainText: decodedSearchParams?.searchItem?.format.mainText || place?.name,
        secondaryText: decodedSearchParams?.searchItem?.format.secondaryText || '',
      }),
    }), [place?.name, decodedSearchParams?.searchItem, searchItemPlaceId])
  const { globalHotelsSearchDispatch, globalHotelsSearchState } = useGlobalHotelsSearchContext({
    ...GLOBAL_SEARCH_INITIAL_STATE,
    searchType: 'destination',
    searchItem,
    onBedbankSelectDatesPrompt,
    searchTargetLabel: place?.name,
  }, queryParams)

  const parseCanonicalName = (canonicalName: string) => {
    const splittedCanonicalName = canonicalName.split(', ')
    return (splittedCanonicalName.length === 1 ? 'country' : 'location') + '/' + splittedCanonicalName.join('/')
  }

  useEffect(() => {
    if (!place && location) {
      for (const params of offerListsGroup) {
        dispatch(fetchPlaceByName(params.location))
      }
    }
  }, [dispatch, location, place, offerListsGroup])

  useEffect(() => {
    if (productType === 'Hotels' && place) {
      // we need to append these to the search string to ensure that filters work
      dispatch(replaceSearchString({ destinationName: place.name, destinationId: place.id, adults: 2, children: 'none', flexibleWithDates: true }))
    }
  }, [place, dispatch, productType])

  const parsedSearchParams = useMemo(() => parseSearchString(windowSearch), [windowSearch])
  const searchKey = getSearchKey(parsedSearchParams)
  const { searchId } = useSearchEvent(searchKey)

  const filters = useMemo((): App.OfferListFilters => {
    return {
      ...filtersFromUrl,
      destinationId: placeId,
      offerTypes: [offerListsGroup[0].offerType],
      distanceEq: 0,
    }
  }, [placeId, offerListsGroup, filtersFromUrl])

  const searchContext = useMemo(() => {
    return mapGlobalSearchContextToSnowplowSearchEvent({
      ...globalHotelsSearchState,
      eventAnalytics: { searchId },
      searchVerticals: new Set([searchVertical]),
      searchItem,
    })
  }, [globalHotelsSearchState, searchId, searchVertical, searchItem])

  const tracking = useOfferListTracking(
    getListName('OfferList', 'Destination Product', 'Main Results'),
    {
      ga: {
        category: 'OfferList',
        label: 'Main Results',
      },
      key: getOfferListKey(filters),
    },
  )
  const offerList = useOfferList(filters)
  const destinationProductListKey = offerList.key
  const [offer] = useOffer(offerList.offerIds[0])

  const isDesktop = useScreenSizeUp('largeDesktop')
  const enabledFilters: EnabledFilters = { ...defaultEnabledFilters, priceFilter: false }
  const { toggleSearchVertical } = useGlobalSearchURLHashVertical()
  const openSearchMenu = useCallback(() => { toggleSearchVertical(searchVertical) }, [toggleSearchVertical, searchVertical])

  const extraOfferListFilters: Array<App.OfferListFilters> = useMemo(() => {
    return offerListsGroup.filter((offerList) => locationToPlace[offerList.location]).map((offerList) => {
      return {
        ...filtersFromUrl,
        destinationId: locationToPlace[offerList.location]?.id,
        offerTypes: [offerList.offerType],
        distanceEq: 0,
      }
    })
  }, [offerListsGroup, locationToPlace, filtersFromUrl])
  const filtersList = [filters, ...extraOfferListFilters]

  useEffect(() => {
    filtersList.forEach(filter => {
      dispatch(fetchOfferList(filter))
    })
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersList])
  const extraOfferLists = useAppSelector(state =>
    filtersList.map(group => {
      const key = getOfferListKey(group)
      return state.offer.offerLists[key]
    }),
  shallowEqual)

  const masterOfferList = useMemo((): OfferListAndAction => {
    return {
      action: OfferListAction.OVERRIDE,
      offerList: {
        ...offerList,
        offerIds: unique([
          ...offerList.offerIds,
          ...nonNullable(extraOfferLists.filter(offerList => offerList)).flatMap(offerList => offerList.offerIds),
        ]),
      },
    }
  }, [offerList, extraOfferLists])
  const mergedFiltersList = useMultipleOfferListFilters(filtersList)

  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(searchUrl, 'sortBy')}`, [searchUrl])
  const showFilterPane = isDesktop

  const handleFilterChange = useCallback<FilterControlChangeHandler>((params) => {
    dispatch(setSearchEvent(
      getSearchKey(parseSearchString(params.search)),
      { searchEvent: { type: 'filters' } },
    ))
    dispatch(pushWithRegion('/search', params.search, undefined, params.hash))
  }, [dispatch])

  const handleSortChange = useCallback<SortControlChangeHandler>((params) => {
    dispatch(setSearchEvent(
      getSearchKey(parseSearchString(params.search)),
      { searchEvent: { type: 'sort' } },
    ))
    dispatch(pushWithRegion('/search', params.search))
  }, [dispatch])

  const offerListInterstitials: Array<OfferListV2Item> = useMemo(() => {
    const interstitials: Array<OfferListV2Item> = [
      {
        id: 'heading',
        position: 'start' as const,
        element:
  <CSSBreakpoint min="tablet">
    <OfferCountAndSort
      windowSearch={windowSearch}
      mainListTotal={offerList.offerCount!}
      sortOptions={searchSortByTypes}
      filters={filters}
      fetching={offerList.fetching}
      destinationName={place?.name}
      mapViewUrl={mapViewURL}
      shouldShowFilters={!showFilterPane}
      searchType="destination"
      showOfferCount={false}
      onFilterChange={handleFilterChange}
      onSortChange={handleSortChange}
    />
  </CSSBreakpoint>,

        show: !offerList.fetching && productType === 'Hotels',
      },
      {
        id: 'submit',
        position: 'end',
        element: <Typography align="center"><TextButton kind="primary" onClick={handleSearchFormSubmit} size="large">{intl.formatMessage({
          id: 'o-k3eVlNAyiQpWh7nrtf',
          defaultMessage: 'View more great offers',
          description: 'Button text to view more offers on ads landing page',
        })}
        </TextButton>
        </Typography>,
      },
    ]

    return interstitials
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [extraOfferListFilters, filters, offerList, showFilterPane, handleSearchFormSubmit])
  const onListEvent = useCallback<OfferListEventHandler>((dispatchAction) => {
    // we only want to fire this event on the main offer list
    // and not the extra offer list to avoid dup events
    if (dispatchAction.type === OfferListEvents.listLoad && destinationProductListKey === dispatchAction.key) {
      trackEvent(searchEvent('offer-list-load', `destination-${searchVertical}-product-page`, {
        filters,
        searchItem: decodedSearchParams.searchItem,
        vertical: searchVertical,
      }))
    }
  }, [destinationProductListKey, filters, decodedSearchParams.searchItem, searchVertical])

  const [tracked, setTracked] = useState<Set<string>>(new Set())
  const { trackSearchEvent } = useSearchAnalytics({ searchContext })

  const listKey = useMemo(() => {
    if (filters) {
      return getOfferListKey(filters)
    }
    return ''
  }, [filters])

  useEffect(() => {
    if (filters.destinationId !== ANYWHERE_PLACE_ID && filters.rooms?.length && !tracked.has(listKey)) {
      trackSearchEvent()
      setTracked(prev => new Set([...prev, listKey]))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [listKey, filters])

  if (place === null || (location === undefined)) {
    return <HttpStatus code={404}><Error404PageWithOffers /></HttpStatus>
  }
  const relatedTravelItems = getRelatedTravelItems(location, undefined, productType, canonicalName, intl)

  if (!place) {
    filters.destinationId = ANYWHERE_PLACE_ID
  }

  const navItems: Array<DestinationNavItemType> = []
  return <GlobalSearchContextProvider
    state={globalHotelsSearchState}
    dispatch={globalHotelsSearchDispatch}
  >
    <HeadData
      canonical={productType.toLowerCase() === 'hotels' && location ? `/${productType.toLowerCase()}/${urlTransform(parseCanonicalName(location))}` : undefined}
      imageId={offer && 'images' in offer && offer.images[0] ? offer.images[0].id : undefined}
    />
    <Breadcrumbs location={location} holidayType={undefined} productType={productType} regionCode={regionCode} />
    {searchFormEnabled && (
      <>
        <DesktopContainer>
          <SearchForm
            submitOnApply
            filters={filters}
            ref={formRef}
            showDateFieldOnMobile={false}
            typeaheadTypes={HOTEL_SEARCH_TYPEAHEAD_TYPES}
          />
        </DesktopContainer>
        <MobileStyledSearchField
          filters={filters}
          sortOptions={searchSortByTypes}
          onEdit={openSearchMenu}
          overrideAvailableFilters={mergedFiltersList}
          onFilterChange={handleFilterChange}
          onSortChange={handleSortChange}
        />
      </>
    )}
    {!placeLoading && (
      <OfferListEventsProvider
        tracking={tracking}
        onListEvent={onListEvent}
      >
        <StyledLayoutContainer size={showFilterPane ? 'xl' : 'lg'} className={clsx({ filterPane: showFilterPane, fullWidth: true })}>
          <StyledVerticalSpacer gap={16}>
            <OfferList
              extraOfferListAndAction={masterOfferList}
              tileStyle="search"
              interstitials={offerListInterstitials}
            />
          </StyledVerticalSpacer>
          {showFilterPane &&
            <StyledHotelSearchPageFilterPane
              filters={filters}
              overrideAvailableFilters={mergedFiltersList}
              search={searchUrl}
              enabledFilters={enabledFilters}
              mapViewURL={mapViewURL}
              onFilterChange={handleFilterChange}
            />}
        </StyledLayoutContainer>
      </OfferListEventsProvider>
    )}
    {productType === 'Hotels' && location && navItems.length > 0 && <Sheet padding="16 0 24">
      <TopDestinationList destinationNavItems={navItems} />
    </Sheet>}
    {location && <Faq
      type="destinationProduct"
      maxToShow={10}
      slug={`${urlTransform(location).toLowerCase()}`}
      tags={[productType]}
      wrapper={FaqWrapper}
    />}
    {productType === 'Hotels' && relatedTravelItems.length > 1 && <DestinationPageRelatedTravelItems items={relatedTravelItems} />}
  </GlobalSearchContextProvider>
}

const mapStateToProps = (state: App.State) => ({
  locationToPlace: state.destination.locationToPlace,
  placesLoading: state.destination.placesLoading,
  regionCode: state.geo.currentRegionCode,
  isSearchEnabled: isSearchEnabled(),
  customHeadings: state.content?.secureHeadingParams?.landing,
  windowSearch: state.router.location.search,
  filtersFromUrl: getUrlOfferListFilters(state),
})

export default connect(mapStateToProps)(AdsLandingPage)
